import React from 'react';
import { Box, useTheme } from '@mui/material';

import { Text, TextField } from 'components';
import { formatPrice } from 'core/shared';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { useTranslations } from 'hooks/useTranslations';

import { UseMutation, UseQuery } from 'useQuery';

import { FrequencyField, SectionSaveFooter } from './InvoicingShared';

interface OrganisationUsageProps {
  organisationId: string;
}

function OrganisationUsage({ organisationId }: OrganisationUsageProps) {
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();
  const translations = useTranslations('organisationUsage');
  const { addToast } = useToast();

  const {
    data: usageData,
    refetch: refetchUsage,
    isLoading: loadingUsage,
  } = UseQuery.organisationUsage({ id: organisationId });
  const { data: contract, refetch: refetchContract } = UseQuery.invoicingContract({
    id: organisationId,
  });

  const { mutateAsync: mutateAsyncUpdateUsageFeeConfig, isLoading: loadingUpdate } =
    UseMutation.updateOrganisationUsageFeeConfig();
  const { mutateAsync: updateContract, isLoading: loadingContract } =
    UseMutation.updateInvoicingContract();

  const [localFees, setLocalFees] = React.useState<Record<string, number>>({});
  const [localStartingFrom, setLocalStartingFrom] = React.useState<Record<string, number>>({});
  const [frequencyMonths, setFrequencyMonths] = React.useState(1);

  React.useEffect(() => {
    if (usageData?.result?.metrics) {
      const fees: Record<string, number> = {};
      const startingFrom: Record<string, number> = {};
      usageData.result.metrics.forEach((metric) => {
        fees[metric.fee_type] = metric.fee_amount;
        startingFrom[metric.fee_type] = metric.starting_from;
      });
      setLocalFees(fees);
      setLocalStartingFrom(startingFrom);
    }
  }, [usageData]);

  React.useEffect(() => {
    setFrequencyMonths(contract?.usage_frequency_months ?? 1);
  }, [contract]);

  const feesChanged = React.useMemo(() => {
    if (!usageData?.result?.metrics) {
      return false;
    }
    return usageData.result.metrics.some(
      (metric) =>
        localFees[metric.fee_type] !== metric.fee_amount ||
        localStartingFrom[metric.fee_type] !== metric.starting_from,
    );
  }, [localFees, localStartingFrom, usageData]);

  const frequencyChanged = frequencyMonths !== (contract?.usage_frequency_months ?? 1);
  const hasChanges = feesChanged || frequencyChanged;

  function handleFeeChange(feeType: string, value: number) {
    setLocalFees((prev) => ({
      ...prev,
      [feeType]: Math.round(value * 100),
    }));
  }

  function handleStartingFromChange(feeType: string, value: number) {
    setLocalStartingFrom((prev) => ({
      ...prev,
      [feeType]: Math.max(0, Math.round(value)),
    }));
  }

  async function save() {
    try {
      if (feesChanged) {
        const fees = Object.entries(localFees).map(([fee_type, fee_amount]) => ({
          fee_type,
          fee_amount,
          starting_from: localStartingFrom[fee_type] ?? 0,
        }));
        await mutateAsyncUpdateUsageFeeConfig({ id: organisationId, fees });
      }
      if (frequencyChanged) {
        await updateContract({
          id: organisationId,
          contract: { usage_frequency_months: frequencyMonths },
        });
      }
      await Promise.all([refetchUsage(), refetchContract()]);
      addToast({ title: translations.updatedSuccessfully, severity: 'success' });
    } catch (error) {
      // handled by the mutations' onError toasts
    }
  }

  // Live within-section totals based on the draft values (independent of saving).
  function rowTotalCents(feeType: string, count: number): number {
    const fee = localFees[feeType] ?? 0;
    const free = localStartingFrom[feeType] ?? 0;
    return Math.max(0, count - free) * fee;
  }

  const totalCents = (usageData?.result?.metrics ?? []).reduce(
    (sum, metric) => sum + rowTotalCents(metric.fee_type, metric.count),
    0,
  );

  function feeTypeLabel(feeType: string) {
    return (translations as Record<string, string>)[feeType] ?? feeType;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        padding: 2,
        marginBottom: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
      }}
    >
      <Text.Bold>{translations.title}</Text.Bold>
      <Box sx={{ paddingTop: 0.5 }}>
        <Text.Small color="gray">{translations.subtitle}</Text.Small>
      </Box>

      <Box sx={{ paddingTop: 2, maxWidth: 480 }}>
        <FrequencyField months={frequencyMonths} onChange={setFrequencyMonths} />
      </Box>

      <Box sx={{ overflowX: 'auto', paddingTop: 1 }}>
        <Box sx={{ minWidth: 640 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              paddingY: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Text.Bold>{translations.feeType}</Text.Bold>
            </Box>
            <Box sx={{ width: 80, flexShrink: 0 }}>
              <Text.Bold>{translations.count}</Text.Bold>
            </Box>
            <Box sx={{ width: 140, flexShrink: 0 }}>
              <Text.Bold>{translations.feeAmount}</Text.Bold>
            </Box>
            <Box sx={{ width: 120, flexShrink: 0 }}>
              <Text.Bold>{translations.startingFrom}</Text.Bold>
            </Box>
            <Box sx={{ width: 110, flexShrink: 0 }}>
              <Text.Bold>{translations.total}</Text.Bold>
            </Box>
          </Box>

          {usageData?.result?.metrics.map((metric) => (
            <Box
              key={metric.fee_type}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                paddingY: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Text>{feeTypeLabel(metric.fee_type)}</Text>
              </Box>
              <Box sx={{ width: 80, flexShrink: 0 }}>
                <Text>{metric.count}</Text>
              </Box>
              <Box sx={{ width: 140, flexShrink: 0 }}>
                <TextField
                  type="number"
                  label=""
                  value={(localFees[metric.fee_type] ?? 0) / 100}
                  onChange={(value) => handleFeeChange(metric.fee_type, value)}
                  fullWidth
                  adornment="EUR"
                />
              </Box>
              <Box sx={{ width: 120, flexShrink: 0 }}>
                <TextField
                  type="number"
                  label=""
                  value={localStartingFrom[metric.fee_type] ?? 0}
                  onChange={(value) => handleStartingFromChange(metric.fee_type, value)}
                  fullWidth
                />
              </Box>
              <Box sx={{ width: 110, flexShrink: 0 }}>
                <Text>
                  {formatPrice({
                    price: rowTotalCents(metric.fee_type, metric.count),
                    symbol: true,
                  })}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 2 }}>
        <Text.Bold>
          {`${translations.totalUsageFee}: ${formatPrice({ price: totalCents, symbol: true })}`}
        </Text.Bold>
      </Box>

      <SectionSaveFooter
        dirty={hasChanges}
        saving={loadingUpdate || loadingContract}
        disabled={loadingUsage}
        onSave={save}
      />
    </Box>
  );
}

export { OrganisationUsage };
